import React from 'react';
import axios from 'axios';

import CountDown from './components/CountDown';
import Cart from './components/Cart';
import { RETRIABLE_ERRORS } from '@/lib/components/PspComposer/PspComposer';

import Loader from '@/lib/components/Loader';
import CallbackPaymentBlock from '@/lib/components/CallbackPaymentBlock';
import PspComposer from '@/lib/components/PspComposer';
import usePaymentPage from '@/payment/usePaymentPage';
import useLocale from '@/payment/useLocale';

import styles from './PaymentPage.module.scss';
import BuyersInformation from '@/lib/components/BuyersInformation';
import PaymentHeader from '@/lib/components/PaymentHeader';
import { SaveCardContext } from '@/lib/context/SaveCardContext';
import CartPaymentPlan from './components/CartPaymentPlan';

const PaymentPage = () => {
    const {
        t,
        integrationRef,
        lyriaUrl,
        displayCallbackBlock,
        setDisplayCallbackBlock,
        isPaymentSuccess,
        setIsPaymentSuccess,
        paymentRequestToken,
        setPaymentRequestToken,
        redirectURL,
        setRedirectURL,
        summaryLoading,
        setSummaryLoading,
        isTimeUp,
        setIsTimeUp,
        order,
        setOrder,
        errorCode,
        setErrorCode,
    } = usePaymentPage();

    const { locale, setLocaleOption } = useLocale();

    const [loader, setLoader] = React.useState(<Loader />);
    const [donationEnabled, setDonationEnabled] = React.useState(false);
    const [shouldRedirect, setShouldRedirect] = React.useState(false);
    const [isCheckoutPlan, setIsCheckoutPlan] = React.useState(false);
    const [showSaveCardMessage, setShowSaveCardMessage] = React.useState(false);
    const [displayChangePaymentMethodButton, setDisplayChangePaymentMethodButton] = React.useState(false);

    const canCancel = !isPaymentSuccess && !isCheckoutPlan && !isTimeUp;

    let CartComponent = null;

    if (paymentRequestToken) {
        CartComponent = isCheckoutPlan ? (
            <CartPaymentPlan
                summaryLoading={summaryLoading}
                paymentRequestToken={paymentRequestToken}
                schedulePlan={paymentRequestToken?.instalment_plan?.instalment_schedule}
                total={paymentRequestToken?.instalment_plan?.total}
                locale={locale}
            />
        ) : (
            <Cart
                summaryLoading={summaryLoading}
                paymentRequestToken={paymentRequestToken}
            />
        );
    }


    const paymentRequestUUID = React.useMemo(() => {
        const path = window.location.pathname;
        const parts = path.split('/');
        return parts[parts.length - 1];
    }, []);

    React.useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                window.location.reload();
            }
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => {
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, []);

    React.useEffect(() => {
        if (paymentRequestUUID) {
            axios({
                method: 'GET',
                url: `${lyriaUrl}/payments/paymentrequests/${paymentRequestUUID}`,
            })
                .then((paymentRequestData) => {
                    setOrder(paymentRequestData.data.order);
                    setPaymentRequestToken(paymentRequestData.data.payment_info);
                    setShowSaveCardMessage(paymentRequestData.data.payment_info?.must_accept_recurring_payments);
                    setIsCheckoutPlan(Object.keys(paymentRequestData.data.payment_info?.instalment_plan).length > 0);
                    const urlParams = new URLSearchParams(window.location.search);
                    setLocaleOption(paymentRequestData.data.payment_info.locale || urlParams.get('lng'));
                    setSummaryLoading(false);
                    if (paymentRequestData.data.payment_info?.time_remaining_seconds <= 0) {
                        setDisplayCallbackBlock(true);
                        setRedirectURL(paymentRequestData.data.payment_info?.failure_callback_url);
                        setShouldRedirect(true);
                        setIsTimeUp(true);
                    }
                });
        }

    }, [lyriaUrl, paymentRequestUUID, setLocaleOption, setPaymentRequestToken, setSummaryLoading, isPaymentSuccess, setOrder, setDisplayCallbackBlock, setRedirectURL, setIsTimeUp]);

    const handleCancelPayment = React.useCallback(() => {
        if (paymentRequestToken?.cancel_callback_url) {
            window.location.href = paymentRequestToken.cancel_callback_url;
        } else {
            window.history.go(-1);
        }
    }, [paymentRequestToken]);

    const handleChangePaymentMethod = React.useCallback(() => {
        setDisplayChangePaymentMethodButton(false);
        window.location.reload();
    }, []);

    const onPaymentResult = React.useCallback((res) => {
        if (res.success) {
            setLoader(null);
        }
        setIsPaymentSuccess(res.success);

        if (!res.success
            && (RETRIABLE_ERRORS.includes(res.data?.code)
                || RETRIABLE_ERRORS.includes(res.data?.message)
            )
        ) {
            return;
        }
        setDisplayCallbackBlock(true);
        setDonationEnabled(res.donationEnabled);
        setIsTimeUp(res.isTimeUp);
        setErrorCode(res.data?.code);
        const newRedirectUrl = res.success ? paymentRequestToken?.success_callback_url : paymentRequestToken?.failure_callback_url;
        if (!newRedirectUrl) {
            return;
        }
        setRedirectURL(newRedirectUrl);
        if (!res.donationEnabled) {
            setShouldRedirect(true);
        }
    }, [setErrorCode, setIsPaymentSuccess, setDisplayCallbackBlock, setIsTimeUp, paymentRequestToken?.success_callback_url, paymentRequestToken?.failure_callback_url, setRedirectURL]);

    return (
        <div className={styles.body}>
            <PaymentHeader />
            <div className={styles.content}>
                <div className={styles.content_left}>
                    <div className={styles.payment}>
                        <div className={styles.payment_method}>
                            {displayCallbackBlock ?
                                <CallbackPaymentBlock
                                    isPaymentSuccess={isPaymentSuccess}
                                    redirectURL={redirectURL}
                                    isTimeUp={isTimeUp}
                                    donationEnabled={donationEnabled}
                                    shouldRedirect={shouldRedirect}
                                    setShouldRedirect={setShouldRedirect}
                                    lyriaUrl={lyriaUrl}
                                    paymentRequestId={paymentRequestUUID}
                                    locale={locale}
                                    isCheckoutPlan={isCheckoutPlan}
                                    paymentRequestToken={paymentRequestToken}
                                    errorCode={errorCode}
                                /> :
                                <SaveCardContext.Provider
                                    value={{
                                        showSaveCardMessage,
                                        setShowSaveCardMessage,
                                    }}>
                                    {
                                        paymentRequestToken && paymentRequestToken.time_remaining_seconds > 0 && <PspComposer
                                            ref={integrationRef}
                                            lang={locale}
                                            loader={loader}
                                            lyriaUrl={lyriaUrl}
                                            paymentRequestId={paymentRequestUUID}
                                            onPaymentResult={onPaymentResult}
                                            setDisplayCallbackBlock={setDisplayCallbackBlock}
                                            successCallbackUrl={paymentRequestToken?.success_callback_url}
                                            cancelCallbackUrl={paymentRequestToken?.cancel_callback_url}
                                            failureCallbackUrl={paymentRequestToken?.failure_callback_url}
                                            bankStatement={paymentRequestToken?.bank_statement}
                                            paymentRequestToken={paymentRequestToken}
                                            isCheckoutPlan={isCheckoutPlan}
                                            setDisplayChangePaymentMethodButton={setDisplayChangePaymentMethodButton}
                                            order={order}
                                        />
                                    }
                                </SaveCardContext.Provider>
                            }
                        </div>
                    </div>
                    <div className={styles.button_footer}>
                        {canCancel &&
                        <button className={styles.cancel_button} onClick={handleCancelPayment}>
                            {t('cancelPayment')}
                        </button>
                        }

                        {displayChangePaymentMethodButton &&
                            <button className={styles.cancel_button} onClick={handleChangePaymentMethod}>
                                {t('changePaymentMethod')}
                            </button>
                        }
                    </div>
                </div>
                <div className={styles.content_right}>
                    {!isPaymentSuccess && paymentRequestToken?.time_remaining_seconds !== undefined && paymentRequestToken?.failure_callback_url &&
                        <CountDown timeRemaining={paymentRequestToken.time_remaining_seconds} onPaymentResult={onPaymentResult} redirectURL={paymentRequestToken.failure_callback_url} />
                    }
                    {CartComponent}
                    {paymentRequestToken?.last_name && paymentRequestToken?.first_name && paymentRequestToken?.email &&
                        <BuyersInformation paymentRequestToken={paymentRequestToken} />
                    }
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;
